import React, { useMemo, useRef, useState, useEffect } from 'react';
import classes from './VendorBuilder.module.scss';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import PageLayout from 'new/elements/pageLayout/PageLayout';
import { ScreenConfig } from 'new/routes/RouterManager';
import * as PATHS from 'js/constants/pathnames';
import Tooltip from 'js/components/elements/mui/Tooltip';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { useBottomDistance } from 'new/hooks/useBottomDistance';
import {
  useVendorBuilder,
  VendorBuilderContext,
} from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { General } from 'new/components/settings/VendorBuilder/General';
import { VendorInfo } from 'new/components/settings/VendorBuilder/VendorInfo';
import { AuthTemplates } from 'new/components/settings/VendorBuilder/AuthConfig/AuthTemplates';
import { Endpoints } from 'new/components/settings/VendorBuilder/Endpoints/Endpoints';
import parseVendorToVendorSpec from 'new/components/settings/VendorBuilder/parseVendorToVendorSpec';
import { Button } from 'new/elements/Button';
import { Errors } from 'new/components/settings/VendorBuilder/Errors';
import theme from './solarizedDark.json';
import { CheckRounded, ClearRounded, FileCopyRounded } from '@mui/icons-material';
import { fallbackCopyTextToClipboard } from 'js/lib/MiscUtils';
import { Prompt } from 'react-router-dom';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import Typography from 'new/elements/Typography';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useHistory } from 'react-router-dom';
import { isNil } from 'lodash';
import UserPage from 'new/components/settings/userPage/UserPage';

interface VendorBuilderProps {
  onResetKey: any;
}

const monacoOptions = {
  readOnly: true,
  minimap: { enabled: false },
};

function setupTheme(monaco) {
  monaco.editor.defineTheme('solarizedDark', theme);
  monaco.editor.setTheme('solarizedDark');
}

const VendorBuilderLocal = (props: VendorBuilderProps) => {
  const [showErrorContainer, setShowErrorContainer] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const height = useBottomDistance(containerRef, 500);
  const user = useCurrentUser();
  const history = useHistory();
  const { vendor, onValidateVendor, errors, onClear } = useVendorBuilder();

  useEffect(() => {
    if (!isNil(user) && !user.super_user) {
      history.replace(UserPage.screenConfig.path);
    }
  }, [user]);

  const monacoValue = useMemo(() => {
    return parseVendorToVendorSpec(vendor);
  }, [vendor]);

  const onClickValidate = () => {
    setShowErrorContainer(true);
    onValidateVendor();
  };
  const onClickCopy = async () => {
    setShowErrorContainer(true);
    onValidateVendor();
    await fallbackCopyTextToClipboard(monacoValue);
  };

  const onCloseErrors = () => {
    setShowErrorContainer(false);
  };

  const onClickClear = (e) => {
    e.stopPropagation();

    setShowResetDialog(true);
  };

  const onConfirmClear = () => {
    onClear();
    props.onResetKey();
  };

  const onResetDialogAction = (e, confirmed = false) => {
    e.stopPropagation();
    setShowResetDialog(false);
    if (confirmed) {
      onConfirmClear();
    }
  };

  const resetDialogView = (
    <Dialog
      key="dialog-cta"
      open={showResetDialog}
      fullWidth
      maxWidth="sm"
      onClose={(e) => onResetDialogAction(e, false)}
    >
      <DialogTitle>Reset Template Form?</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Please confirm if you would like to reset the template form page and start from scratch.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="info" onClick={(e) => onResetDialogAction(e, false)}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={(e) => onResetDialogAction(e, true)}>
          Reset the Form
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <PageLayout
      ref={containerRef}
      title="Templatized Connector Builder"
      rightView={
        <Stack direction="row" alignItems="center" gap="16px">
          <Button
            color="inherit"
            variant="outlined"
            onClick={onClickClear}
            startIcon={<ClearRounded />}
          >
            Reset
          </Button>
          {showResetDialog && resetDialogView}
          <Button
            color="success"
            variant="outlined"
            startIcon={<CheckRounded />}
            onClick={onClickValidate}
          >
            Validate
          </Button>
          <Tooltip
            arrow={true}
            tooltipTheme="fieldInfo"
            rootStyle={{}}
            title={
              <>
                <h3>Copy & Submit</h3>
                <ol
                  style={{
                    fontSize: 'var(--font-size-base)',
                    lineHeight: '120%',
                  }}
                >
                  <li>Copy the generated JSON to the Clipboard</li>
                  <li>Save the copied content in Github repository.</li>
                  <li>
                    <code>Note: The generated spec may still have issues you need to resolve.</code>
                  </li>
                  <li>Modify the generated template as necessary.</li>
                  <li>Submit a Github PR with your new spec.</li>
                </ol>
              </>
            }
          >
            <Button startIcon={<FileCopyRounded />} variant="contained" onClick={onClickCopy}>
              Copy to Clipboard
            </Button>
          </Tooltip>
        </Stack>
      }
      className={classes.container}
      pageClassName={classes.rootPageContainer}
      description="Easily create new templatized connector json specs"
    >
      <Stack direction="row" height={height}>
        <Prompt
          when={!isNil(user) && user?.super_user === true}
          message="Are you sure you want to exit this connector creation?"
        />

        <Stack width="60%" overflow="auto" flex={1} height="100%">
          <Stack maxWidth={400}>
            <KnowledgePanel
              alwaysShowUnderline
              knowledgeContent={
                <Typography padding="16px">
                  <strong>Detailed steps</strong>
                  <br />
                  <ol>
                    <li>
                      Click through different sections and add all necessary information.
                      <br />
                      Refer to the Coda Pages and [API
                      reference](https://developers.nexla.com/reference) as needed.
                    </li>
                    <li>
                      Click <code>Validate</code> button as needed. This runs some basic validations
                      to catch mistakes.
                    </li>
                    <li>
                      Click <code>Copy to Clipboard</code> when ready. This will copy the spec.{' '}
                      <br />
                      You can also just copy the spec from the code browser.
                    </li>
                    <li>In the GitHub repo, create a new branch as per usual instructions.</li>
                    <li>Pase the copied spec in the spec file.</li>
                    <li>
                      <strong>Do a visual inspection of the spec and fix any mistakes</strong>
                    </li>
                    <li>Add logo files, notes etc.</li>
                    <li>Submit your branch and raise a PR.</li>
                    <li>
                      <strong>
                        NOTE! This spec is only saved to local browser storage while you are working
                        on it, not backed up on a database
                      </strong>
                    </li>
                    <li>
                      Click <strong>Reset</strong> if you want to clear out the screen and start a
                      new spec.
                    </li>
                  </ol>
                </Typography>
              }
            >
              <Typography variant="h6" padding="16px" gap="16px">
                <strong>Steps for Creating a New Template</strong>
              </Typography>
            </KnowledgePanel>
          </Stack>
          <Typography padding="16px" variant="body2">
            <ol>
              <li>Use this screen to generate the desired spec.</li>
              <li>
                <code>Copy to Clipboard</code> when ready. This will copy the spec & run validations
                <br />
              </li>
              <li>
                Paste this spec in Github Branch, along with other necessary files (logos, notes,
                etc).
                <strong>
                  Do a visual inspection of the spec and fix any mistakes. Validators are not
                  foolproof.
                </strong>
              </li>
              <li>Submit your branch and raise a PR.</li>
            </ol>
          </Typography>
          <General />
          <VendorInfo />
          <AuthTemplates />
          <Endpoints />
        </Stack>
        <Stack width="40%" className={classes.editorContainer}>
          <LoadableMonacoEditor
            ref={editorRef}
            language={'json'}
            value={monacoValue}
            theme="solarizedDark"
            height={height}
            beforeMount={setupTheme}
            options={monacoOptions}
          />

          {showErrorContainer ? (
            Object.keys(errors).length ? (
              <Errors onClose={onCloseErrors} />
            ) : (
              <Stack style={{ backgroundColor: 'lightgreen' }}>
                <Typography padding="16px">
                  Congrats! Looks like there no validation errors in your spec. You can copy it to
                  the clipboard and submit it to GitHub as a PR.
                  <br />
                  <br />
                  <strong>Please note:</strong>
                  <br />
                  There might still be some errors this validator has not been able to catch.
                  <code>
                    Please do a visual inspection of the copied spec while submitting as GitHub PR
                  </code>
                </Typography>
              </Stack>
            )
          ) : null}
        </Stack>
      </Stack>
    </PageLayout>
  );
};

export const VendorBuilder = (props) => {
  const [vendorKey, setVendorKey] = useState<any>('vendor' + Math.random());

  const onResetKey = () => {
    setVendorKey('vendor' + Math.random());
  };

  return (
    <VendorBuilderContext {...props}>
      <VendorBuilderLocal key={vendorKey} {...props} onResetKey={onResetKey} />
    </VendorBuilderContext>
  );
};

export const vendorBuilderScreenConfig = {
  screenName: 'settings.VendorBuilder',
  path: `${PATHS.ROUTE_REDESIGN}/settings/connector-builder`,
  header: {
    title: 'Connector Builder',
    slugTitle: 'Connector builder',
  },
  domain: 'settings',
} as ScreenConfig<VendorBuilderProps>;

VendorBuilder.screenConfig = vendorBuilderScreenConfig;
